import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, ChevronDown, ArrowLeft, KeyRound } from "lucide-react";
import { COUNTRIES } from "../lib/countries";
import { useCountryAutoDetect } from "../lib/useCountryAutoDetect";

/**
 * "Book Your Second Opinion" widget on the landing page.
 *
 * Flow:
 *   Step 1  — patient enters name / country / phone / age / city
 *   Step 2  — INDIA phones only: OTP is sent, patient verifies, we auto-
 *             create their patient account (via /auth/otp/verify), attach
 *             the picked country, and redirect to /patient so they land
 *             directly on the booking wizard Step 1 (Choose Doctor).
 *
 *   For INTERNATIONAL phones (non-IN) the existing lead-capture path is
 *   preserved (POST /leads/callback) — we can't send India-only Fast2SMS
 *   OTPs to +44 / +1 / etc. Our advisor calls them back within 1 hr.
 */
// COUNTRIES imported from /lib/countries.js above (single source of truth).

export default function BookOpinionForm({ source = "landing-hero" }) {
  const nav = useNavigate();
  const { otpSend, otpVerify } = useAuth();
  const [step, setStep] = useState(1); // 1 = details, 2 = OTP verify
  const [form, setForm] = useState({
    name: "", country: "IN", phone: "", age: "", city: "",
  });
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [otpChannel, setOtpChannel] = useState("sms"); // sms | whatsapp | demo
  const [otpFails, setOtpFails] = useState(0);         // count wrong OTP attempts
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Live doctor availability — refreshed every 30s so the scarcity nudge
  // ("🟢 X specialists available now") stays honest without hammering the DB.
  const [availability, setAvailability] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/doctors/live-availability", { timeout: 3000 });
        if (!cancelled) setAvailability(r.data || null);
      } catch (e) { /* silent — badge just stays hidden */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Auto-detect country: IP → browser locale → India fallback. Runs once
  // on mount and overrides only if we don't already have a country selection.
  const { country: detectedCountry } = useCountryAutoDetect("IN");
  useEffect(() => {
    if (detectedCountry && detectedCountry !== form.country) {
      setForm((f) => ({ ...f, country: detectedCountry }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedCountry]);

  const country = COUNTRIES.find((c) => c.iso === form.country) || COUNTRIES[0];
  const isIndia = country.iso === "IN";

  const phoneIntl = (() => {
    const clean = form.phone.replace(/[\s\-()]/g, "");
    return clean.startsWith(country.dial.replace("+", "")) ? `+${clean}` : `${country.dial}${clean}`;
  })();

  // Step 1 → validate + always send OTP (India via SMS, others via WhatsApp)
  const submitDetails = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error("Please enter your full name"); return; }
    const phoneClean = form.phone.replace(/[\s\-()]/g, "");
    if (!/^\d{7,14}$/.test(phoneClean)) { toast.error("Enter a valid phone number"); return; }
    if (!form.age || Number(form.age) < 1 || Number(form.age) > 120) { toast.error("Please enter a valid age"); return; }
    if (!form.city.trim()) { toast.error("Please enter your city"); return; }
    setBusy(true);
    try {
      // Always capture the lead (admin dashboard funnel) — fire-and-forget.
      api.post("/leads/callback", {
        name: form.name.trim(), phone: phoneIntl, country: form.country,
        age: Number(form.age), city: form.city.trim(), source,
      }).catch(() => {});

      // Send OTP → India (SMS via Fast2SMS), International (WhatsApp via Interakt template)
      const r = await otpSend(phoneIntl);
      setDemoOtp(r.otp_demo || "");
      setOtpChannel(r.channel || (r.sms ? "sms" : "demo"));
      const channelLabel = r.channel === "whatsapp"
        ? "WhatsApp"
        : r.sms ? "SMS"
        : "demo";
      toast.success(r.otp_demo ? `OTP (demo): ${r.otp_demo}` : `OTP sent via ${channelLabel}`);
      setOtpFails(0);
      setStep(2);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  // Step 2 → verify OTP, auto-create patient, land on booking wizard
  const submitOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(otp)) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      await otpVerify(
        phoneIntl,
        otp,
        form.name.trim(),
        Number(form.age),
        null,
        form.country,
      );
      toast.success("Verified! Let's pick your specialist.");
      nav("/patient");
    } catch (err) {
      setOtpFails((n) => n + 1);
      toast.error(fmtErr(err.response?.data?.detail) || "Invalid OTP");
    } finally { setBusy(false); }
  };

  // Task 4 — After 2 wrong OTP attempts, show a "Continue on WhatsApp"
  // button that hands the patient off to the WABA (24/7 desk).
  const waFallbackHref = `https://wa.me/918317674160?text=${encodeURIComponent(
    `Hi, I'm ${form.name || "a patient"} from ${country.name}. My OTP didn't work — please help me book a second opinion. Phone: ${phoneIntl}`,
  )}`;

  // Resend OTP (India only, uses same 45s cooldown as login)
  const resendOtp = async () => {
    setBusy(true);
    try {
      const r = await otpSend(phoneIntl);
      setDemoOtp(r.otp_demo || "");
      toast.success(r.sms ? "New OTP sent" : `OTP (demo): ${r.otp_demo}`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not resend OTP");
    } finally { setBusy(false); }
  };

  // ── DONE (international callback path only) ─────────────────────────
  if (done) {
    return (
      <Card className="p-6 border-blue-200 bg-white" data-testid="book-opinion-done">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700 mb-3">
          <CheckCircle2 className="w-6 h-6"/>
        </div>
        <h3 className="font-heading text-lg font-semibold text-slate-900">Thank you, {form.name.split(" ")[0]}!</h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          A health advisor will reach you on <b className="text-slate-900">{country.dial} {form.phone}</b> within
          the next business hour. Keep your MRI / CT scans handy.
        </p>
        <a href="https://wa.me/918317674160" target="_blank" rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 text-sm font-semibold hover:underline"
          data-testid="book-opinion-wa-link">
          Or chat now on WhatsApp →
        </a>
      </Card>
    );
  }

  // ── STEP 2 — OTP verification (India) ────────────────────────────────
  if (step === 2) {
    return (
      <Card className="p-6 sm:p-7 border-slate-200 bg-white shadow-md" data-testid="book-opinion-otp">
        <button type="button" onClick={() => setStep(1)}
          className="text-xs font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-3"
          data-testid="book-otp-back">
          <ArrowLeft className="w-3.5 h-3.5"/> Change number
        </button>
        <h3 className="font-heading text-2xl font-bold text-slate-900 leading-tight">
          Verify your mobile
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          {otpChannel === "whatsapp"
            ? <>We sent a 6-digit code on <b className="text-emerald-700">WhatsApp</b> to <b className="text-slate-900">{phoneIntl}</b>. Open WhatsApp to see it.</>
            : <>We sent a 6-digit code to <b className="text-slate-900">{phoneIntl}</b>.</>}
        </p>
        <form onSubmit={submitOtp} className="mt-5 space-y-4">
          <FloatField label="6-digit OTP" required>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
              <Input
                required
                autoFocus
                inputMode="numeric"
                maxLength={8}
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, ""))}
                data-testid="book-otp-input"
                className="h-11 pl-9 border-slate-300 tracking-[6px] font-mono text-lg"
              />
            </div>
          </FloatField>

          {demoOtp && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-[12px] text-amber-800">
              <b>Demo OTP:</b> <span className="font-mono">{demoOtp}</span> (SMS delivery isn&apos;t live yet in this environment)
            </div>
          )}

          <Button type="submit" disabled={busy}
            className="w-full h-12 bg-[#0B5ED7] hover:bg-[#084BB0] text-white font-bold tracking-wide text-base shadow-md"
            data-testid="book-otp-submit">
            {busy ? "Verifying…" : "VERIFY & CONTINUE"}
          </Button>
          <button type="button" onClick={resendOtp} disabled={busy}
            className="w-full text-xs text-slate-500 hover:text-slate-700"
            data-testid="book-otp-resend">
            Didn&apos;t get it? Resend OTP
          </button>

          {/* Task 4 — WhatsApp fallback after 2 failed OTP attempts */}
          {otpFails >= 2 && (
            <a
              href={waFallbackHref}
              target="_blank"
              rel="noreferrer"
              data-testid="book-otp-wa-fallback"
              className="mt-2 flex items-center justify-center gap-2 w-full h-11 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm"
            >
              💬 Continue on WhatsApp instead
            </a>
          )}
        </form>
      </Card>
    );
  }

  // ── STEP 1 — Details ─────────────────────────────────────────────────
  return (
    <Card className="p-6 sm:p-7 border-slate-200 bg-white shadow-md" data-testid="book-opinion-form">
      <h3 className="font-heading text-2xl font-bold text-slate-900 leading-tight">
        Book Your Second Opinion
      </h3>
      <p className="text-[12.5px] text-slate-500 mt-1">
        Verify with OTP — India via SMS, other countries via WhatsApp
      </p>

      {/* Live doctor availability — scarcity nudge, refreshed every 30s. */}
      {availability && availability.total_approved > 0 && (
        <div
          data-testid="live-doctor-availability-badge"
          className="mt-3 inline-flex items-center gap-2 text-xs font-medium rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-800"
          title={`${availability.available_now} of ${availability.total_approved} approved specialists ready to review your case right now.`}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600"></span>
          </span>
          <span data-testid="live-doctor-availability-count">
            {availability.available_now === 1
              ? "1 neuro specialist available now"
              : `${availability.available_now} neuro specialists available now`}
          </span>
        </div>
      )}
      <form onSubmit={submitDetails} className="mt-5 space-y-4">
        {/* Full Name */}
        <FloatField label="Full Name" required>
          <Input required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="book-name-input" className="h-11 border-slate-300" />
        </FloatField>

        {/* Country + Phone */}
        <div className="grid grid-cols-5 gap-3">
          <div className="col-span-2">
            <FloatField label="Country" required>
              <div className="relative">
                <select value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  data-testid="book-country-select"
                  className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 pr-9 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#0B5ED7]/30 focus:border-[#0B5ED7]">
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>{c.iso} {c.name} ({c.dial})</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"/>
              </div>
            </FloatField>
          </div>
          <div className="col-span-3">
            <FloatField label="Phone Number" required>
              <div className="flex">
                <span className="inline-flex items-center px-3 h-11 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-600 text-sm font-medium">
                  {country.dial}
                </span>
                <Input required inputMode="tel" placeholder="Enter your phone number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d\s\-()]/g, "") })}
                  data-testid="book-phone-input"
                  className="h-11 rounded-l-none border-slate-300"/>
              </div>
            </FloatField>
          </div>
        </div>

        {/* Age + City */}
        <div className="grid grid-cols-2 gap-3">
          <FloatField label="Age" required>
            <Input required type="number" min={1} max={120} inputMode="numeric"
              value={form.age}
              onChange={(e) => setForm({ ...form, age: e.target.value })}
              data-testid="book-age-input" className="h-11 border-slate-300"/>
          </FloatField>
          <FloatField label="City" required>
            <Input required value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="book-city-input" className="h-11 border-slate-300"/>
          </FloatField>
        </div>

        <div className="flex items-center gap-1.5 text-[12px] text-slate-500 pt-1">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/>
          Your information is secure and protected
        </div>

        <Button type="submit" disabled={busy}
          className="w-full h-12 bg-[#0B5ED7] hover:bg-[#084BB0] text-white font-bold tracking-wide text-base shadow-md"
          data-testid="book-submit-btn">
          {busy ? "Sending…" : (isIndia ? "SEND OTP & CONTINUE" : "SEND WHATSAPP OTP")}
        </Button>

        {/* Prefer-to-talk shortcut for patients who don't want the OTP flow */}
        <a
          href="tel:+919441724072"
          data-testid="book-assist-phone"
          className="mt-2 flex items-center justify-center gap-1.5 text-[12px] text-slate-500 hover:text-[#0B5ED7]"
        >
          📞 Prefer to talk? Call our assistance line: <b className="text-slate-700">+91 94417 24072</b>
        </a>
      </form>
    </Card>
  );
}

function FloatField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
